import { request } from '@/api/client';
import type {
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  ForgotPasswordResponse,
  EmailVerificationPendingResponse,
  VerifyEmailResponse,
  ResendVerificationResponse,
} from '@/types';

/** Authentication endpoints — login, register, OAuth, email verification, password reset. */
export const authService = {
  login: (email: string, password: string): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password } as LoginRequest),
    }),

  register: (data: RegisterRequest): Promise<AuthResponse | EmailVerificationPendingResponse> =>
    request<AuthResponse | EmailVerificationPendingResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  refresh: (): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/refresh', {
      method: 'POST',
      auth: true,
    }),

  googleLogin: (accessToken: string, role = 'fan'): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken, role }),
    }),

  appleLogin: (
    idToken: string,
    userData?: { name?: { firstName?: string; lastName?: string }; email?: string },
    role = 'fan'
  ): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken, user_data: userData, role }),
    }),

  forgotPassword: (email: string): Promise<ForgotPasswordResponse> =>
    request<ForgotPasswordResponse>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (
    token: string,
    new_password: string
  ): Promise<{ success: boolean; message: string }> =>
    request<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      // Backend Pydantic model requires confirm_password to match new_password.
      body: JSON.stringify({ token, new_password, confirm_password: new_password }),
    }),

  verifyEmail: (token: string): Promise<VerifyEmailResponse> =>
    request<VerifyEmailResponse>(`/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    }),

  resendVerification: (email: string): Promise<ResendVerificationResponse> =>
    request<ResendVerificationResponse>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
};
